import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileSearch,
  Info,
  Loader2,
  Play,
  Plus,
  RefreshCw,
  XCircle,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTenantId } from '@/lib/auth/hooks';
import { CubixApiError } from '@/lib/api/types';
import { cn } from '@/lib/utils';

import {
  useCreateStressScenario,
  useLatestStressRuns,
  useRunStressScenario,
  useStressScenarios,
} from './api';
import {
  fmtDelta,
  fmtRatio,
  SCENARIO_TYPE_LABELS,
  STATUS_LABELS,
  type CreateStressScenarioRequest,
  type ScenarioType,
  type StressRun,
  type StressScenario,
} from './types';

// ── Helpers ───────────────────────────────────────────────────────────────────

const EMPTY_FORM: CreateStressScenarioRequest = {
  name: '',
  description: '',
  scenarioType: 'MANUAL',
  rateShockBp: null,
  nimShockBp: null,
  lcrRunoffRetailStable: null,
  lcrRunoffRetailLessStable: null,
  lcrRunoffWholesaleOperational: null,
  lcrRunoffWholesaleNonOperational: null,
  lcrHqlaLevel2aHaircut: null,
  lcrHqlaLevel2bHaircut: null,
};

// ── Main page ─────────────────────────────────────────────────────────────────

/**
 * Phase 2.4.STRESS — Stress Testing page.
 *
 * Layout:
 *   1. "New scenario" collapsible form
 *   2. Scenario list with inline Run button
 *   3. Latest runs table (last 20 across all scenarios)
 *
 * Clicking "Run" on a scenario immediately triggers the engine and shows
 * the result inline via a detail expansion row.
 */
export function StressScenariosPage() {
  const tenantId = useTenantId();
  const [showForm, setShowForm]         = useState(false);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  const scenarios  = useStressScenarios();
  const latestRuns = useLatestStressRuns();

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Stress Testing</h1>
          <p className="text-sm text-muted-foreground">
            Define shock scenarios and run them against live baseline metrics · tenant{' '}
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{tenantId ?? '—'}</code>.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? (
            <ChevronUp className="mr-2 h-4 w-4" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          {showForm ? 'Cancel' : 'New scenario'}
        </Button>
      </header>

      {showForm && (
        <CreateScenarioForm onCreated={() => setShowForm(false)} />
      )}

      <ScenarioList
        scenarios={scenarios.data ?? []}
        isLoading={scenarios.isLoading}
        error={scenarios.error}
        onRefresh={() => scenarios.refetch()}
        expandedRunId={expandedRunId}
        onExpandRun={setExpandedRunId}
      />

      <LatestRunsCard
        runs={latestRuns.data ?? []}
        isLoading={latestRuns.isLoading}
        onRefresh={() => latestRuns.refetch()}
        expandedRunId={expandedRunId}
        onExpandRun={setExpandedRunId}
      />
    </div>
  );
}

// ── Create scenario form ──────────────────────────────────────────────────────

function CreateScenarioForm({ onCreated }: { onCreated: () => void }) {
  const [form, setForm] = useState<CreateStressScenarioRequest>(EMPTY_FORM);
  const create = useCreateStressScenario({ onSuccess: onCreated });

  function set<K extends keyof CreateStressScenarioRequest>(
    key: K,
    value: CreateStressScenarioRequest[K],
  ) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const payload: CreateStressScenarioRequest = {
      ...form,
      // send nulls for empty shock fields so server uses tenant defaults
      rateShockBp: form.rateShockBp != null ? Number(form.rateShockBp) : null,
      nimShockBp:  form.nimShockBp  != null ? Number(form.nimShockBp)  : null,
    };
    create.mutate(payload);
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">New stress scenario</CardTitle>
        <CardDescription>
          Leave shock fields empty to inherit the tenant's live ALM parameters at run time.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {create.error && (
          <div className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {create.error instanceof CubixApiError
                ? create.error.message
                : 'Failed to create scenario'}
            </span>
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          {/* Name + type */}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="sc-name">Scenario name *</Label>
              <Input
                id="sc-name"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="e.g. +300bp shock"
                required
                maxLength={100}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sc-type">Type</Label>
              <select
                id="sc-type"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-ring"
                value={form.scenarioType ?? 'MANUAL'}
                onChange={(e) => set('scenarioType', e.target.value as ScenarioType)}
              >
                <option value="MANUAL">Manual</option>
                <option value="BAM_STANDARD">BAM Standard</option>
                <option value="CRISIS">Crisis</option>
                <option value="REGULATORY">Regulatory</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="sc-desc">Description</Label>
            <Input
              id="sc-desc"
              value={form.description ?? ''}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Optional description"
            />
          </div>

          {/* Rate + NIM shocks */}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="sc-rate">Rate shock (bp)</Label>
              <Input
                id="sc-rate"
                type="number"
                value={form.rateShockBp ?? ''}
                onChange={(e) =>
                  set('rateShockBp', e.target.value !== '' ? Number(e.target.value) : null)
                }
                placeholder="e.g. 200  (leave blank = tenant default)"
                min={-1000}
                max={1000}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sc-nim">NIM shock (bp)</Label>
              <Input
                id="sc-nim"
                type="number"
                value={form.nimShockBp ?? ''}
                onChange={(e) =>
                  set('nimShockBp', e.target.value !== '' ? Number(e.target.value) : null)
                }
                placeholder="e.g. 200  (leave blank = tenant default)"
                min={-1000}
                max={1000}
              />
            </div>
          </div>

          {/* LCR run-off overrides */}
          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground">
              LCR run-off rate overrides (decimal, e.g. 0.10 = 10 %) — leave blank to use tenant params
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              {(
                [
                  ['lcrRunoffRetailStable',            'Retail stable'],
                  ['lcrRunoffRetailLessStable',         'Retail less-stable'],
                  ['lcrRunoffWholesaleOperational',     'Wholesale operational'],
                  ['lcrRunoffWholesaleNonOperational',  'Wholesale non-operational'],
                ] as const
              ).map(([key, label]) => (
                <div key={key} className="space-y-1.5">
                  <Label htmlFor={`sc-${key}`}>{label}</Label>
                  <Input
                    id={`sc-${key}`}
                    type="number"
                    step="0.0001"
                    min={0}
                    max={1}
                    value={form[key] ?? ''}
                    onChange={(e) =>
                      set(key, e.target.value !== '' ? e.target.value : null)
                    }
                    placeholder="e.g. 0.10"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* HQLA haircut overrides */}
          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground">
              HQLA haircut overrides (decimal) — leave blank to use tenant params
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              {(
                [
                  ['lcrHqlaLevel2aHaircut', 'Level 2A haircut'],
                  ['lcrHqlaLevel2bHaircut', 'Level 2B haircut'],
                ] as const
              ).map(([key, label]) => (
                <div key={key} className="space-y-1.5">
                  <Label htmlFor={`sc-${key}`}>{label}</Label>
                  <Input
                    id={`sc-${key}`}
                    type="number"
                    step="0.0001"
                    min={0}
                    max={1}
                    value={form[key] ?? ''}
                    onChange={(e) =>
                      set(key, e.target.value !== '' ? e.target.value : null)
                    }
                    placeholder="e.g. 0.15"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={create.isPending}>
              {create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create scenario
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ── Scenario list ─────────────────────────────────────────────────────────────

function ScenarioList({
  scenarios,
  isLoading,
  error,
  onRefresh,
  expandedRunId,
  onExpandRun,
}: {
  scenarios: StressScenario[];
  isLoading: boolean;
  error: CubixApiError | null;
  onRefresh: () => void;
  expandedRunId: string | null;
  onExpandRun: (id: string | null) => void;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="text-base">Scenarios</CardTitle>
          <CardDescription>
            Click <strong>Run</strong> to execute a scenario against the latest baseline data.
          </CardDescription>
        </div>
        <Button variant="ghost" size="icon" onClick={onRefresh} title="Refresh">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading scenarios…
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            {error instanceof CubixApiError ? error.message : 'Failed to load scenarios'}
          </div>
        )}
        {!isLoading && !error && scenarios.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No scenarios yet. Use the "New scenario" button above to create one.
          </p>
        )}
        {scenarios.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">Name</th>
                  <th className="pb-2 pr-4 font-medium">Type</th>
                  <th className="pb-2 pr-4 font-medium">Rate shock</th>
                  <th className="pb-2 pr-4 font-medium">NIM shock</th>
                  <th className="pb-2 pr-4 font-medium">Status</th>
                  <th className="pb-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {scenarios.map((s) => (
                  <ScenarioRow
                    key={s.id}
                    scenario={s}
                    expandedRunId={expandedRunId}
                    onExpandRun={onExpandRun}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ScenarioRow({
  scenario,
  expandedRunId,
  onExpandRun,
}: {
  scenario: StressScenario;
  expandedRunId: string | null;
  onExpandRun: (id: string | null) => void;
}) {
  const [lastRunId, setLastRunId] = useState<string | null>(null);
  const run = useRunStressScenario({
    onSuccess: (data) => {
      setLastRunId(data.id);
      onExpandRun(data.id);
    },
  });

  const isExpanded = lastRunId !== null && expandedRunId === lastRunId;

  return (
    <>
      <tr className="group">
        <td className="py-2.5 pr-4">
          <span className="font-medium">{scenario.name}</span>
          {scenario.description && (
            <p className="text-xs text-muted-foreground">{scenario.description}</p>
          )}
        </td>
        <td className="py-2.5 pr-4">
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
            {SCENARIO_TYPE_LABELS[scenario.scenarioType]}
          </span>
        </td>
        <td className="py-2.5 pr-4 tabular-nums">
          {scenario.rateShockBp != null ? `${scenario.rateShockBp > 0 ? '+' : ''}${scenario.rateShockBp} bp` : '—'}
        </td>
        <td className="py-2.5 pr-4 tabular-nums">
          {scenario.nimShockBp != null ? `${scenario.nimShockBp > 0 ? '+' : ''}${scenario.nimShockBp} bp` : '—'}
        </td>
        <td className="py-2.5 pr-4">
          <StatusBadge status={scenario.status} />
        </td>
        <td className="py-2.5">
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={run.isPending}
              onClick={() => run.mutate({ scenarioId: scenario.id })}
            >
              {run.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Play className="mr-1.5 h-3.5 w-3.5" />
              )}
              Run
            </Button>
            {lastRunId && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  onExpandRun(isExpanded ? null : lastRunId)
                }
              >
                {isExpanded ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </Button>
            )}
          </div>
          {run.error && (
            <p className="mt-1 text-xs text-destructive">
              {run.error instanceof CubixApiError
                ? run.error.message
                : 'Run failed'}
            </p>
          )}
        </td>
      </tr>
      {isExpanded && lastRunId && (
        <RunDetailRow runId={lastRunId} colSpan={6} />
      )}
    </>
  );
}

// ── Run detail expansion ──────────────────────────────────────────────────────

function RunDetailRow({ runId, colSpan }: { runId: string; colSpan: number }) {
  // Run data is already in the latestRuns cache — avoid a redundant fetch
  const run = useLatestStressRuns();
  const detail = run.data?.find((r) => r.id === runId) ?? null;

  if (!detail) return null;

  return (
    <tr>
      <td colSpan={colSpan} className="pb-4 pt-1">
        <div className="rounded-lg border bg-muted/30 p-4">
          <RunResultGrid run={detail} />
        </div>
      </td>
    </tr>
  );
}

// ── Latest runs card ──────────────────────────────────────────────────────────

function LatestRunsCard({
  runs,
  isLoading,
  onRefresh,
  expandedRunId,
  onExpandRun,
}: {
  runs: StressRun[];
  isLoading: boolean;
  onRefresh: () => void;
  expandedRunId: string | null;
  onExpandRun: (id: string | null) => void;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="text-base">Recent runs</CardTitle>
          <CardDescription>Last 20 stress runs across all scenarios.</CardDescription>
        </div>
        <Button variant="ghost" size="icon" onClick={onRefresh} title="Refresh">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading runs…
          </div>
        )}
        {!isLoading && runs.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No runs yet. Select a scenario above and click <strong>Run</strong>.
          </p>
        )}
        {runs.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">Scenario</th>
                  <th className="pb-2 pr-4 font-medium">Date</th>
                  <th className="pb-2 pr-4 font-medium text-right">LCR (stressed)</th>
                  <th className="pb-2 pr-4 font-medium text-right">NSFR (stressed)</th>
                  <th className="pb-2 pr-4 font-medium text-right">NIM delta</th>
                  <th className="pb-2 pr-4 font-medium">Compliant</th>
                  <th className="pb-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {runs.map((r) => (
                  <>
                    <tr key={r.id} className="group">
                      <td className="py-2.5 pr-4 font-medium">{r.scenarioName}</td>
                      <td className="py-2.5 pr-4 tabular-nums text-muted-foreground">
                        {r.calculationDate}
                      </td>
                      <td className="py-2.5 pr-4 text-right tabular-nums">
                        {r.lcrDataAvailable ? fmtRatio(r.stressedLcrRatio) : <NoData />}
                      </td>
                      <td className="py-2.5 pr-4 text-right tabular-nums">
                        {r.lcrDataAvailable ? fmtRatio(r.stressedNsfrRatio) : <NoData />}
                      </td>
                      <td className={cn(
                        'py-2.5 pr-4 text-right tabular-nums',
                        r.nimDelta != null && parseFloat(r.nimDelta) < 0
                          ? 'text-destructive'
                          : 'text-emerald-600',
                      )}>
                        {r.nimDataAvailable ? fmtDelta(r.nimDelta) : <NoData />}
                      </td>
                      <td className="py-2.5 pr-4">
                        <ComplianceBadge
                          lcr={r.stressedLcrCompliant}
                          nsfr={r.stressedNsfrCompliant}
                          lcrAvail={r.lcrDataAvailable}
                        />
                      </td>
                      <td className="py-2.5">
                        <div className="flex items-center gap-1">
                          <Link
                            to={`/reporting/stress/${r.id}`}
                            title="View full stress report"
                            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 hover:underline"
                          >
                            <FileSearch className="h-3.5 w-3.5" />
                            Report
                          </Link>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              onExpandRun(expandedRunId === r.id ? null : r.id)
                            }
                          >
                            {expandedRunId === r.id ? (
                              <ChevronUp className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronDown className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {expandedRunId === r.id && (
                      <tr key={`${r.id}-detail`}>
                        <td colSpan={7} className="pb-4 pt-1">
                          <div className="rounded-lg border bg-muted/30 p-4">
                            <RunResultGrid run={r} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Run result comparison grid ────────────────────────────────────────────────

function RunResultGrid({ run }: { run: StressRun }) {
  type MetricRow = {
    label: string;
    base: string;
    stressed: string;
    delta: string;
    deltaVal: number | null;
    available: boolean;
  };

  const rows: MetricRow[] = [
    {
      label:     'LCR ratio',
      base:      fmtRatio(run.baseLcrRatio),
      stressed:  fmtRatio(run.stressedLcrRatio),
      delta:     fmtDelta(run.lcrDelta),
      deltaVal:  run.lcrDelta != null ? parseFloat(run.lcrDelta) : null,
      available: run.lcrDataAvailable,
    },
    {
      label:     'NSFR ratio',
      base:      fmtRatio(run.baseNsfrRatio),
      stressed:  fmtRatio(run.stressedNsfrRatio),
      delta:     fmtDelta(run.nsfrDelta),
      deltaVal:  run.nsfrDelta != null ? parseFloat(run.nsfrDelta) : null,
      available: run.lcrDataAvailable,
    },
    {
      label:     'NIM',
      base:      fmtRatio(run.baseNimBaseline),
      stressed:  fmtRatio(run.stressedNim),
      delta:     fmtDelta(run.nimDelta),
      deltaVal:  run.nimDelta != null ? parseFloat(run.nimDelta) : null,
      available: run.nimDataAvailable,
    },
    {
      label:     'EVE worst-case',
      base:      fmtRatio(run.baseEveWorstCase),
      stressed:  fmtRatio(run.stressedEveWorstCase),
      delta:     fmtDelta(run.eveDelta),
      deltaVal:  run.eveDelta != null ? parseFloat(run.eveDelta) : null,
      available: run.eveDataAvailable,
    },
  ];

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-medium text-muted-foreground">
          Run on {run.calculationDate} · engine {run.engineVersion} · triggered by {run.triggeredBy}
        </p>
        {!run.lcrDataAvailable && !run.nimDataAvailable && !run.eveDataAvailable && (
          <span className="flex items-center gap-1 text-xs text-amber-600">
            <Info className="h-3.5 w-3.5" />
            No baseline data available for this date
          </span>
        )}
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-xs text-muted-foreground">
            <th className="pb-2 pr-6 text-left font-medium">Metric</th>
            <th className="pb-2 pr-6 text-right font-medium">Baseline</th>
            <th className="pb-2 pr-6 text-right font-medium">Stressed</th>
            <th className="pb-2 text-right font-medium">Δ</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((row) => (
            <tr key={row.label} className={!row.available ? 'opacity-40' : undefined}>
              <td className="py-2 pr-6 font-medium">{row.label}</td>
              <td className="py-2 pr-6 text-right tabular-nums">
                {row.available ? row.base : <NoData />}
              </td>
              <td className="py-2 pr-6 text-right tabular-nums">
                {row.available ? row.stressed : <NoData />}
              </td>
              <td className={cn(
                'py-2 text-right tabular-nums font-medium',
                row.deltaVal == null || !row.available
                  ? 'text-muted-foreground'
                  : row.deltaVal < 0
                  ? 'text-destructive'
                  : 'text-emerald-600',
              )}>
                {row.available ? row.delta : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Small presentational components ──────────────────────────────────────────

function StatusBadge({ status }: { status: StressScenario['status'] }) {
  const colors: Record<StressScenario['status'], string> = {
    DRAFT:    'bg-muted text-muted-foreground',
    ACTIVE:   'bg-emerald-100 text-emerald-700',
    ARCHIVED: 'bg-amber-100 text-amber-700',
  };
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-xs', colors[status])}>
      {STATUS_LABELS[status]}
    </span>
  );
}

function ComplianceBadge({
  lcr,
  nsfr,
  lcrAvail,
}: {
  lcr: boolean | null;
  nsfr: boolean | null;
  lcrAvail: boolean;
}) {
  if (!lcrAvail) return <NoData />;
  const both = lcr && nsfr;
  const neither = lcr === false || nsfr === false;
  if (both)
    return (
      <span className="flex items-center gap-1 text-xs text-emerald-600">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Compliant
      </span>
    );
  if (neither)
    return (
      <span className="flex items-center gap-1 text-xs text-destructive">
        <XCircle className="h-3.5 w-3.5" />
        Breach
      </span>
    );
  return <span className="text-xs text-muted-foreground">Partial</span>;
}

function NoData() {
  return <span className="text-xs text-muted-foreground">n/a</span>;
}
